import { useCallback, useEffect, useState } from 'react';

export type ToastKind = 'success' | 'info' | 'warn' | 'error' | 'pending';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  ttlMs?: number;
}

// Simple module-scoped event bus so any component can push a toast.
const listeners = new Set<(t: Toast) => void>();
let counter = 0;

export function pushToast(t: Omit<Toast, 'id'>): string {
  const id = `t_${Date.now()}_${counter++}`;
  for (const fn of listeners) fn({ id, ttlMs: 4000, ...t });
  return id;
}

export function useToasts() {
  const [items, setItems] = useState<Toast[]>([]);

  useEffect(() => {
    const onPush = (t: Toast) => {
      setItems((prev) => [...prev, t]);
      if (t.ttlMs && t.ttlMs > 0) {
        setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), t.ttlMs);
      }
    };
    listeners.add(onPush);
    return () => {
      listeners.delete(onPush);
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts: items, dismiss };
}
